/**
 * HourlyPricingEditor — shared admin/hotel-admin UI block for configuring
 * per-slot enable + price on a room.
 *
 * Controlled component. Reads `enabled` (boolean) + `slots` (array of
 * { slot_id, price, enabled }) and emits via onChange.
 */
import React from 'react';
import { Clock } from 'lucide-react';
import { HOURLY_SLOTS } from '../data/hourlySlots';

const ensureSlot = (slots, slotId) => {
  const found = slots.find((s) => s.slot_id === slotId);
  if (found) return found;
  return { slot_id: slotId, price: 0, enabled: false };
};

export const HourlyPricingEditor = ({ enabled, slots, onChange, testIdPrefix = 'hourly' }) => {
  const safeSlots = Array.isArray(slots) ? slots : [];

  const setEnabled = (b) => onChange({ enabled: b, slots: safeSlots });
  const updateSlot = (slotId, patch) => {
    const merged = HOURLY_SLOTS.map((s) => {
      const cur = ensureSlot(safeSlots, s.id);
      return s.id === slotId ? { ...cur, ...patch } : cur;
    });
    onChange({ enabled, slots: merged });
  };
  const enabledCount = HOURLY_SLOTS.filter((s) => ensureSlot(safeSlots, s.id).enabled).length;

  return (
    <div data-testid={`${testIdPrefix}-editor`} className="rounded-2xl border-2 border-dashed border-[#FFE5EE] bg-[#FFFBFD] p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-9 h-9 rounded-full bg-[#FF0059] text-white flex items-center justify-center flex-shrink-0">
            <Clock className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <p className="font-black text-sm text-black leading-tight">Hourly slot pricing</p>
            <p className="text-[11px] text-gray-500 leading-tight">
              {enabled ? `${enabledCount}/8 slots active` : 'Optional — let guests book 3-hour slots'}
            </p>
          </div>
        </div>
        <label className="inline-flex items-center cursor-pointer flex-shrink-0">
          <input
            type="checkbox"
            checked={!!enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="sr-only peer"
            data-testid={`${testIdPrefix}-toggle`}
          />
          <div className="relative w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-[#FF0059] after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5"></div>
        </label>
      </div>

      {enabled && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
          {HOURLY_SLOTS.map((s) => {
            const cur = ensureSlot(safeSlots, s.id);
            return (
              <div
                key={s.id}
                className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition-colors ${
                  cur.enabled ? 'border-[#FF0059] bg-white' : 'border-gray-200 bg-white/60'
                }`}
                data-testid={`${testIdPrefix}-row-${s.id}`}
              >
                <label className="flex items-center gap-1.5 cursor-pointer flex-shrink-0">
                  <input
                    type="checkbox"
                    checked={!!cur.enabled}
                    onChange={(e) => updateSlot(s.id, { enabled: e.target.checked })}
                    data-testid={`${testIdPrefix}-on-${s.id}`}
                  />
                </label>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-black leading-tight whitespace-nowrap">{s.label}</p>
                  <p className="text-[10px] text-gray-500 leading-tight">{s.start} – {s.end}</p>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <span className="text-xs font-bold text-gray-500">₹</span>
                  <input
                    type="number"
                    min="0"
                    step="50"
                    value={cur.price ?? 0}
                    onChange={(e) => updateSlot(s.id, { price: Number(e.target.value) || 0 })}
                    className="w-20 px-2 py-1 rounded-lg border border-gray-200 focus:border-[#FF0059] outline-none text-xs font-bold text-right"
                    data-testid={`${testIdPrefix}-price-${s.id}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
